import React from 'react';
import { Card, Flex, Skeleton } from 'antd';

interface BillingPlanSkeletonProps {
  isMobile?: boolean;
  /** How many agent cards to render as placeholders. */
  count?: number;
  /** Render the section header (title + search) above the cards. */
  showHeader?: boolean;
}

/** Loading placeholder for the "Usage by agent" section — mirrors the agent card layout. */
export const BillingPlanSkeleton: React.FC<BillingPlanSkeletonProps> = ({
  isMobile = false,
  count = 3,
  showHeader = false,
}) => {
  return (
    <Flex vertical gap={16} style={{ width: '100%' }}>
      {/* Section header + search */}
      {showHeader && (
        <Flex justify="space-between" align={isMobile ? 'flex-start' : 'center'} gap={12}>
          <Skeleton.Input active style={{ width: 160, height: 24 }} />
          <Skeleton.Input active style={{ width: isMobile ? '100%' : 240, height: 32 }} />
        </Flex>
      )}

      {/* Agent cards */}
      {Array(count)
        .fill(0)
        .map((_, i) => (
          <Card
            key={i}
            style={{
              background: 'var(--silia-bg-container)',
              border: '1px solid var(--silia-border-subtle)',
              borderRadius: '12px',
            }}
            bodyStyle={{ padding: '20px' }}
          >
            <Flex vertical gap={16}>
              {/* Agent header: icon chip + name + pill + actions */}
              <Flex gap={10} align="center" wrap="wrap">
                <Skeleton.Avatar active shape="square" size={32} />
                <Skeleton.Input active size="small" style={{ width: 160, height: 18 }} />
                <Skeleton.Button active size="small" style={{ width: 150, height: 26 }} />
                <Flex gap={12} style={{ marginLeft: 'auto' }}>
                  <Skeleton.Button active size="small" style={{ width: 100, height: 32 }} />
                  <Skeleton.Button active size="small" style={{ width: 110, height: 32 }} />
                </Flex>
              </Flex>

              {/* Billing metrics sub-panel */}
              <Flex
                vertical
                gap={12}
                style={{
                  background: 'var(--silia-bg-elevated)',
                  border: '1px solid var(--silia-border-subtle)',
                  borderRadius: '10px',
                  padding: '14px 16px',
                }}
              >
                {Array(3)
                  .fill(0)
                  .map((_, r) => (
                    <Flex key={r} justify="space-between" align="center" gap={16}>
                      <Skeleton.Input active size="small" style={{ width: 140, height: 12 }} />
                      <Skeleton.Input active size="small" style={{ width: 90, height: 14 }} />
                    </Flex>
                  ))}
              </Flex>
            </Flex>
          </Card>
        ))}
    </Flex>
  );
};
